import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Edit, User, Briefcase, MapPin, Phone, Mail, CreditCard, FileText, Laptop } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../utils/tailwindUtils';

const EmployeeDetailsContent = ({ employeeData, employeeID }) => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);

    const handleEdit = () => {
        let employeeEditPath = location.pathname === '/employee/viewDetails'
        let candidateEditPath = location.pathname === '/employee/onboarding/viewDetails'
        let internEditPath = location.pathname === '/intern/onboarding/viewDetails'

        // condition based navgation 
        navigate(
            employeeEditPath ? `/employee/add` :
                candidateEditPath ? `/candidate/add` :
                    internEditPath ? `/intern/add` :
                        `/employee/add`,
            {
                state: {
                    employeeId: employeeID,
                    isEdit: true
                }
            });
    };

    let location = useLocation()

    let directEmployeeOnboardFlow = location.pathname === '/employee/viewDetails'

    if (!employeeData) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className="text-gray-500 dark:text-gray-400">No employee data available</p>
            </div>
        );
    }

    const {
        personal_info,
        professional_info,
        bank_info,
        user_creation_info,
        title,
        first_name,
        last_name,
        employee_id
    } = employeeData;

    const employeeId = employee_id || professional_info?.employee_id

    const fullName = `${title || ''} ${first_name || personal_info?.first_name || ''} ${last_name || personal_info?.last_name || ''}`.trim();

    return (
        <div className="space-y-4">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Employee & Professional Information
                    </h2>
                    <p className=" text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete employee profile with personal, professional, and contact details
                    </p>
                </div>
                <Button
                    onClick={handleEdit}
                    className="flex items-center gap-2 bg-sidebar dark:bg-sidebar-primary dark:hover:bg-sidebar-primary/90 hover:bg-sidebar-primary"
                >
                    <Edit className="w-4 h-4" />
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-6">
                            {professional_info?.profile ? (
                                <img
                                    src={professional_info.profile}
                                    alt="Profile"
                                    className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-gray-700 shadow-sm"
                                />
                            ) : (
                                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                    <User className="w-6 h-6" />
                                </div>
                            )}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Personal Information
                                </h3>
                                <p className="text-xs text-blue-600 dark:text-blue-400">Basic personal details</p>
                            </div>
                        </div>

                        {/* Personal Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <InfoField
                                label="Full Name"
                                value={fullName}
                                icon={<User className="w-4 h-4" />}
                            />
                            <InfoField
                                label="Employee ID"
                                value={employeeId}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Date of Birth"
                                value={personal_info?.date_of_birth}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Gender"
                                value={personal_info?.gender}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Blood Group"
                                value={personal_info?.blood_group}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Title"
                                value={personal_info?.title}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                }
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Contact Information</h4>
                                    <p className="text-xs text-green-600 dark:text-green-400">Communication details</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InfoField
                                    label="Mobile Number"
                                    value={personal_info?.mobile_number}
                                    icon={<Phone className="w-4 h-4" />}
                                />
                                <InfoField
                                    label="Email Address"
                                    value={personal_info?.email_address}
                                    icon={
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Professional Information */}
                <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/20 dark:to-pink-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                <Briefcase className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Professional Information
                                </h3>
                                <p className="text-xs text-purple-600 dark:text-purple-400">Work and employment details</p>
                            </div>
                        </div>

                        {/* Professional Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <InfoField
                                label="Display Name"
                                value={professional_info?.display_name}
                                icon={<User className="w-4 h-4" />}
                            />
                            <InfoField
                                label="Department"
                                value={professional_info?.department_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Designation"
                                value={professional_info?.designation_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Reporting Manager"
                                value={professional_info?.reporting_manager_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Employee Type"
                                value={professional_info?.employee_type_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Shift"
                                value={professional_info?.shift_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Work Location"
                                value={professional_info?.work_location}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Employee Group"
                                value={professional_info?.employee_group_name}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                }
                            />
                        </div>

                        {/* Dates Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <InfoField
                                label="Date of Joining"
                                value={professional_info?.date_of_joining}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                }
                            />
                            <InfoField
                                label="Date of Relieving"
                                value={professional_info?.date_of_releving}
                                icon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                }
                            />
                        </div>

                        {/* Status */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <InfoField
                                label="Employment Status"
                                value={
                                    <span className={cn(
                                        "text-sm text-gray-400 dark:text-gray-500 mt-1 px-2 rounded-md",
                                        professional_info?.is_active
                                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800"
                                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800"
                                    )}>
                                        {professional_info?.is_active ? "🟢 Active" : "🔴 Inactive"}
                                    </span>
                                }
                                icon={
                                    professional_info?.is_active ? (
                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Address Information */}
            <div className="space-y-6 mt-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-sidebar-primary/10 text-sidebar-primary">
                        <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Address Information</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Employee's permanent and present addresses</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AddressSection
                        title="Permanent Address"
                        subtitle="Primary residential address"
                        address={personal_info?.permanent_address}
                        city={personal_info?.city}
                        state={personal_info?.state}
                        country={personal_info?.country}
                        pincode={personal_info?.pin_code}
                        type="permanent"
                    />
                    <AddressSection
                        title="Present Address"
                        subtitle="Current residential address"
                        address={personal_info?.present_address}
                        city={personal_info?.present_city}
                        state={personal_info?.present_state}
                        country={personal_info?.present_country}
                        pincode={personal_info?.present_pin_code}
                        type="present"
                    />
                </div>
            </div>

            {/* Contact Person & Emergency Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                {/* Contact Person */}
                <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                <User className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Contact Person</h4>
                                <p className="text-xs text-blue-600 dark:text-blue-400">Primary emergency contact</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <User className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.contact_person || "Not specified"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Contact Person</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <svg className="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.relationship?.charAt(0).toUpperCase() + personal_info?.relationship?.slice(1) || "Not specified"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Relationship</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <Phone className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.contact_person_mobile_number || "Not provided"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Contact Number</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Emergency Contact */}
                <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                <Phone className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Emergency Contact</h4>
                                <p className="text-xs text-red-600 dark:text-red-400">Critical situation contact</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <User className="w-4 h-4 text-red-500 dark:text-red-400" />
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.emergency_contact_person || "Not specified"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Emergency Contact</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <svg className="w-4 h-4 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.emergency_contact_person_relationship?.charAt(0).toUpperCase() + personal_info?.emergency_contact_person_relationship?.slice(1) || "Not specified"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Relationship</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <Phone className="w-4 h-4 text-red-500 dark:text-red-400" />
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">{personal_info?.emergency_contact_person_mobile_number || "Not provided"}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Emergency Number</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Credentials & Bank Information */}
            {
                directEmployeeOnboardFlow ?
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                        {/* User Credentials */}
                        <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                            <div className="relative p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">User Credentials</h4>
                                        <p className="text-xs text-green-600 dark:text-green-400">System access details</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <svg className="w-4 h-4 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{user_creation_info?.user_name || "Not set"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Username</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <svg className="w-4 h-4 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-medium text-gray-900 dark:text-white">
                                                    {user_creation_info?.password ?
                                                        (showPassword ? user_creation_info.password : '•'.repeat(user_creation_info.password.length))
                                                        : "Not specified"
                                                    }
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="ml-2 text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-colors duration-200"
                                                >
                                                    {showPassword ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Password</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <svg className="w-4 h-4 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{user_creation_info?.user_group_name || "Not assigned"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">User Group</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <Mail className="w-4 h-4 text-green-500 dark:text-green-400" />
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{user_creation_info?.profesional_mail || "Not provided"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Professional Email</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Information */}
                        <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                            <div className="relative p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                        <CreditCard className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Bank Information</h4>
                                        <p className="text-xs text-purple-600 dark:text-purple-400">Salary account details</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <svg className="w-4 h-4 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{bank_info?.bank_name || "Not provided"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Bank Name</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <svg className="w-4 h-4 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                        </svg>
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{bank_info?.account_number || "Not provided"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Account Number</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                        <User className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                                        <div>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{bank_info?.account_holder_name || "Not provided"}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Account Holder</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                            <svg className="w-4 h-4 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <div>
                                                <p className="text-xs font-medium text-gray-900 dark:text-white">{bank_info?.ifsc_code || "N/A"}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">IFSC Code</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                            <svg className="w-4 h-4 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <div>
                                                <p className="text-xs font-medium text-gray-900 dark:text-white">{bank_info?.branch || "N/A"}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Branch</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    :
                    null
            }

            {/* Asset Information */}
            {
                directEmployeeOnboardFlow ?
                    <div className="space-y-6 mt-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-sidebar-primary/10 text-sidebar-primary">
                                <Laptop className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Asset Information</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Employee's handovered asset details</p>
                            </div>
                        </div>

                        {/* Asset Cards Grid */}
                        {employeeData?.asset_info && employeeData.asset_info.length > 0 ? (
                            <div className={`grid gap-6 ${employeeData.asset_info.length === 1
                                ? 'grid-cols-1 max-w-2xl'
                                : employeeData.asset_info.length === 2
                                    ? 'grid-cols-1 md:grid-cols-2'
                                    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                }`}>
                                {employeeData.asset_info.map((asset, index) => (
                                    <div
                                        key={asset.id || index}
                                        className={`group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300 ${employeeData.asset_info.length === 1 ? 'w-full' : ''
                                            }`}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/20 dark:to-amber-800/10 opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                                        <div className="relative p-6">
                                            {/* Asset Header */}
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                                                    <Laptop className="w-6 h-6" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                        {asset.asset_registry_name || asset.asset_name || "Unnamed Asset"}
                                                    </h4>
                                                    <p className="text-xs text-orange-600 dark:text-orange-400">
                                                        {asset.asset_id ? `ID: ${asset.asset_id}` : `Asset #${asset.id}`}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Asset Details */}
                                            <div className="space-y-3">
                                                <div className=' grid grid-cols-2 gap-3'>
                                                    {/* Asset Category */}
                                                    {(asset.category_name || asset.sub_category_name) && (
                                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                            <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                            </svg>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-xs font-medium text-gray-900 dark:text-white capitalize">
                                                                    {[asset.category_name, asset.sub_category_name].filter(Boolean).join(' - ')}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Category</p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Asset Condition */}
                                                    {asset.condition && (
                                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                            <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-xs font-medium text-gray-900 dark:text-white capitalize">
                                                                    {asset.condition.toLowerCase()}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Condition</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className=' grid grid-cols-2 gap-3'>
                                                    {/* Issued Date */}
                                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                        <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-medium text-gray-900 dark:text-white">
                                                                {asset.issued_date ? new Date(asset.issued_date).toLocaleDateString('en-IN') : "Not issued"}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">Issued Date</p>
                                                        </div>
                                                    </div>

                                                    {/* Purchase Date */}
                                                    {asset.purchase_date && (
                                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                            <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                            </svg>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-xs font-medium text-gray-900 dark:text-white">
                                                                    {new Date(asset.purchase_date).toLocaleDateString('en-IN')}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Purchase Date</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Handing Over By */}
                                                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                    <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                                            {asset.handing_over_by_name || "Not specified"}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {asset.handing_over_by_employee_id ? `ID: ${asset.handing_over_by_employee_id}` : "Handed Over By"}
                                                        </p>
                                                    </div>
                                                </div>



                                                {/* Remarks */}
                                                {asset.remarks && (
                                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                                        <svg className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                                        </svg>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-medium text-gray-900 dark:text-white">
                                                                {asset.remarks}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">Remarks</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Status Badge */}
                                            <div className="mt-4 flex justify-between items-center">
                                                <span className={cn(
                                                    "text-xs font-medium px-2 py-1 rounded-full border",
                                                    asset.issued_date
                                                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800"
                                                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                                                )}>
                                                    {asset.issued_date ? "✅ Issued" : "⏳ Pending"}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-700/50 px-2 py-1 rounded-md">
                                                    Registry #{asset.asset_registry}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* Empty State */
                            <div className="group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/10 opacity-60"></div>
                                <div className="relative p-8 text-center cursor-pointer" onClick={handleEdit}>
                                    <div className="flex justify-center mb-4">
                                        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500">
                                            <Laptop className="w-8 h-8" />
                                        </div>
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        No Assets Assigned
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                                        This employee hasn't been assigned any assets yet. Assets will appear here once they are assigned and handed over.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    :
                    null
            }
        </div>
    );
};

// Reusable Components
const InfoField = ({ label, value, icon }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm hover:bg-white/70 dark:hover:bg-gray-700/70 transition-colors duration-200">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 flex-shrink-0">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                {label}
            </label>
            <p className="text-xs font-normal text-gray-900 dark:text-white truncate">
                {value || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
            </p>
        </div>
    </div>
);

const AddressSection = ({ title, subtitle, address, city, state, country, pincode, type = "permanent" }) => {
    const hasAddress = address || city || state || country || pincode;

    const getTypeStyles = () => {
        switch (type) {
            case "permanent":
                return {
                    gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/10",
                    iconBg: "bg-blue-100 dark:bg-blue-900/30",
                    iconColor: "text-blue-600 dark:text-blue-400",
                    badgeColor: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                };
            case "present":
                return {
                    gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/10",
                    iconBg: "bg-green-100 dark:bg-green-900/30",
                    iconColor: "text-green-600 dark:text-green-400",
                    badgeColor: "bg-green-500/10 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                };
            default:
                return {
                    gradient: "from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/10",
                    iconBg: "bg-gray-100 dark:bg-gray-900/30",
                    iconColor: "text-gray-600 dark:text-gray-400",
                    badgeColor: "bg-gray-500/10 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800"
                };
        }
    };

    const styles = getTypeStyles();

    return (
        <div className={`group relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${styles.gradient} opacity-60 group-hover:opacity-80 transition-opacity duration-300`}></div>

            <div className="relative p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`flex items-center justify-center w-12 h-12 rounded-xl ${styles.iconBg} ${styles.iconColor}`}>
                            {type === "permanent" ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            )}
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
                        </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles.badgeColor}`}>
                        {type === "permanent" ? "Primary" : "Current"}
                    </span>
                </div>

                {/* Address Content */}
                {hasAddress ? (
                    <div className="space-y-4">
                        {address && (
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                <MapPin className="w-4 h-4 mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-gray-900 dark:text-white leading-relaxed">
                                        {address}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            {(city || state) && (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-xs font-medium text-gray-900 dark:text-white">
                                            {[city, state].filter(Boolean).join(', ')}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">City, State</p>
                                    </div>
                                </div>
                            )}

                            {country && (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-xs font-medium text-gray-900 dark:text-white">{country}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Country</p>
                                    </div>
                                </div>
                            )}

                            {pincode && (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm">
                                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <div>
                                        <p className="text-xs font-medium text-gray-900 dark:text-white">{pincode}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Pincode</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                            <MapPin className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No address provided</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                            {type === "permanent" ? "Primary address not available" : "Current address not available"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeDetailsContent;